import React, { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { adminApi, authStore, isAnalysisRunning, useAuthUser } from "../../api/adminApi";
import AppIcon from "../AppIcon";
import { canAccessPage } from "../../permissions";
import "./app-layout.scss";

const menuItems = [
  { label: "Dashboard", icon: "dashboard", path: "/dashboard", permission: "dashboard" },
  { label: "Conversations", icon: "conversations", path: "/conversations", permission: "conversations" },
  { label: "Tickets", icon: "tickets", path: "/tickets", permission: "tickets" },
  { label: "Analytics", icon: "analytics", path: "/analytics", permission: "analytics" },
  { label: "Chatbot Settings", icon: "settings", path: "/settings", adminsOnly: true },
  { label: "Flow", icon: "flow", path: "/flow", adminsOnly: true },
  { label: "AI Settings", icon: "ai", path: "/ai-settings", adminsOnly: true },
  { label: "Users", icon: "users", path: "/users", adminsOnly: true },
];

const titles = {
  "/dashboard": ["Overview", "Dashboard"],
  "/conversations": ["Visitor history", "Conversations"],
  "/tickets": ["Customer support", "Tickets"],
  "/analytics": ["Visitor behaviour", "Analytics"],
  "/analytics/site-insights": ["Website trends", "Site insights"],
  "/settings": ["Widget content", "Chatbot Settings"],
  "/flow": ["Visitor journey", "Flow"],
  "/ai-settings": ["Traffidio engine", "AI Settings"],
  "/users": ["Team access", "Users"],
  "/no-access": ["Access", "No pages available"],
};

const AppLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [analyticsOpen, setAnalyticsOpen] = useState(() => window.location.pathname.startsWith("/analytics"));
  const user = useAuthUser();
  const site = user.site?.key || "cloudkia";
  const ticketsAllowed = canAccessPage(user, "tickets");
  const [pendingTicketCount, setPendingTicketCount] = useState(0);
  const previousPendingCount = useRef(null);
  const currentPath = useRef(location.pathname);
  const [eyebrow, title] = titles[location.pathname] || titles["/dashboard"];

  useEffect(() => { currentPath.current = location.pathname; }, [location.pathname]);

  useEffect(() => {
    if (location.pathname.startsWith("/analytics")) setAnalyticsOpen(true);
  }, [location.pathname]);

  useEffect(() => {
    if (!ticketsAllowed) { setPendingTicketCount(0); return undefined; }
    let active = true;
    let pending = false;
    const controller = new AbortController();
    const refreshPendingCount = async () => {
      if (!active || pending || isAnalysisRunning()) return;
      pending = true;
      try {
        const result = await adminApi.tickets(site, "waiting", controller.signal, true);
        if (active) {
          const count = Number(result.total || 0);
          if (previousPendingCount.current !== null && count > previousPendingCount.current
            && currentPath.current !== "/tickets" && typeof Notification !== "undefined" && Notification.permission === "granted") {
            new Notification("New support ticket", { body: `${count - previousPendingCount.current} new visitor ticket${count - previousPendingCount.current === 1 ? " is" : "s are"} waiting.` });
          }
          previousPendingCount.current = count;
          setPendingTicketCount(count);
        }
      } catch (_) {} finally { pending = false; }
    };
    void refreshPendingCount();
    const timer = window.setInterval(refreshPendingCount, 10000);
    return () => { active = false; controller.abort(); window.clearInterval(timer); };
  }, [site, ticketsAllowed]);

  useEffect(() => {
    if (!ticketsAllowed) return undefined;
    let active = true;
    let pending = false;
    const controller = new AbortController();
    const heartbeat = () => {
      if (!active || pending || isAnalysisRunning()) return;
      pending = true;
      adminApi.availabilityHeartbeat(site, controller.signal).catch(() => {}).finally(() => { pending = false; });
    };
    heartbeat();
    const timer = window.setInterval(heartbeat, 30000);
    document.addEventListener("visibilitychange", heartbeat);
    return () => {
      active = false;
      controller.abort();
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", heartbeat);
    };
  }, [site, ticketsAllowed]);

  const handleLogout = async () => {
    try { await adminApi.logout(); } catch (_) { /* clear local session regardless */ }
    authStore.clear();
    navigate("/login", { replace: true });
  };

  return <div className="app-layout">
    <aside className={`app-layout__sidebar ${sidebarOpen ? "app-layout__sidebar--open" : ""}`}>
      <div className="app-layout__brand"><div className="app-layout__brand-mark">T</div><div><strong>Traffidio</strong><small>Chatbot Portal</small></div></div>
      <nav className="app-layout__menu">{menuItems.filter(item => item.permission ? canAccessPage(user, item.permission) : (!item.adminsOnly || user.role === "admin" || user.isSuperAdmin)).map(item => item.path === "/analytics" ? <div className="app-layout__menu-group" key={item.path}>
        <button type="button" className={`app-layout__menu-item app-layout__menu-button ${location.pathname.startsWith("/analytics") ? "app-layout__menu-item--active" : ""}`} aria-expanded={analyticsOpen} aria-controls="analytics-submenu" onClick={() => setAnalyticsOpen(open => !open)}><span className="app-layout__menu-icon"><AppIcon name={item.icon}/></span><span className="app-layout__menu-label">Analytics</span><svg className={`app-layout__menu-chevron ${analyticsOpen ? "app-layout__menu-chevron--open" : ""}`} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg></button>
        <div className="app-layout__submenu" id="analytics-submenu" hidden={!analyticsOpen}><NavLink end to="/analytics" className={({ isActive }) => `app-layout__submenu-item ${isActive ? "app-layout__submenu-item--active" : ""}`} onClick={() => setSidebarOpen(false)}>Visitor activity</NavLink><NavLink to="/analytics/site-insights" className={({ isActive }) => `app-layout__submenu-item ${isActive ? "app-layout__submenu-item--active" : ""}`} onClick={() => setSidebarOpen(false)}>Site insights</NavLink></div>
      </div> : <NavLink key={item.path} to={item.path} className={({ isActive }) => `app-layout__menu-item ${isActive ? "app-layout__menu-item--active" : ""}`} onClick={() => setSidebarOpen(false)}><span className="app-layout__menu-icon"><AppIcon name={item.icon}/></span><span className="app-layout__menu-label">{item.label}</span>{item.path === "/tickets" && pendingTicketCount > 0 && <span className="app-layout__menu-badge" aria-label={`${pendingTicketCount} waiting tickets`}>{pendingTicketCount > 99 ? "99+" : pendingTicketCount}</span>}</NavLink>)}</nav>
      <div className="app-layout__service"><span className="app-layout__service-dot"/><div><strong>Platform connected</strong><small>Widget API available</small></div></div>
      <button className="app-layout__logout" type="button" onClick={handleLogout}><AppIcon name="logout" size={18}/>Log out</button>
      <small className="app-layout__powered">Traffidio by Cloudkia</small>
    </aside>
    {sidebarOpen && <button className="app-layout__overlay" type="button" aria-label="Close menu" onClick={() => setSidebarOpen(false)} />}
    <main className="app-layout__main">
      <header className="app-layout__topbar">
        <button className="app-layout__menu-toggle" type="button" aria-label="Open menu" onClick={() => setSidebarOpen(true)}>☰</button>
        <div><p className="app-layout__eyebrow">{eyebrow}</p><h1 className="app-layout__title">{title}</h1></div>
        <div className="app-layout__profile"><span className="app-layout__avatar">{(user.name || "A")[0].toUpperCase()}</span><div><strong>{user.name || "Administrator"}</strong><small>{user.site?.name || "Traffidio"}</small></div></div>
      </header>
      <section className="app-layout__content"><Outlet /></section>
    </main>
  </div>;
};

export default AppLayout;
